import { useEffect, useRef } from 'react';
import Cookies from 'js-cookie';

import { AUTHENTICATION_TOKEN } from 'common/constants';
import { baseUrl } from 'api/API_App';
import { createEngagementCounter } from 'modules/dashboard/helpers/engagementCounter';
import { buildEngagementUrl } from 'modules/dashboard/helpers/engagementUrl';

/**
 * Báo lên BE thời gian trang thật sự hiển thị, để Dashboard tính "thời gian truy cập trung bình".
 *
 * Gửi ĐÚNG hai lúc: khi trang chuyển sang ẩn, và khi rời trang. KHÔNG gửi định kỳ — Google khuyến
 * cáo ping đều đặn tạo ra phiên không bao giờ chết và thổi phồng thời lượng trung bình.
 *
 * Lấy `baseUrl` trực tiếp từ `api/API_App` (đúng nguồn mà `apiClient` đang dùng) thay vì nhận qua
 * tham số, để chỉ có một nguồn sự thật cho URL của BE.
 *
 * Lúc rời trang phải dùng `fetch` với `keepalive` chứ không phải fetch thường: trình duyệt huỷ mọi
 * request đang dở khi trang đóng. `sendBeacon` chỉ là đường lùi cố thêm một lần cho có: nó KHÔNG
 * đặt được header nào, nên không mang được `hvs-token` và sẽ 401 với người đã đăng nhập — tức là
 * KHÔNG tương đương đường `fetch`, đừng coi nó là bảo hiểm.
 */
const useEngagementReporter = () => {
  const counterRef = useRef(createEngagementCounter());

  useEffect(() => {
    const counter = counterRef.current;
    // Phép ghép URL (kèm lý do phải cắt "/" thừa ở cuối) nằm trong helper để test được ngoài DOM.
    const url = buildEngagementUrl(baseUrl);

    // Effect có thể mount sau khi trang đã hiển thị sẵn (không có visibilitychange nào xảy ra
    // trước đó), nên phải tự khởi động phần đếm ngay khi gắn nếu trang đang hiển thị.
    if (document.visibilityState === 'visible') counter.onVisible(Date.now());

    const send = (engagedMs: number) => {
      if (engagedMs <= 0) return;
      const body = JSON.stringify({ engaged_ms: engagedMs });

      // Xác thực y hệt apiClient (interceptor của axios): đọc cookie mà JS đọc được rồi đặt header
      // 'hvs-token', vì CookieToHeaderMiddleware bên BE dựng Authorization từ header đó. Chỉ
      // 'credentials: include' là KHÔNG đủ — cookie do BE đặt là HttpOnly + SameSite=Strict nên
      // request khác site không mang theo, còn cookie của FE thì chỉ gửi về host FE chứ không bao
      // giờ tới host API. Thiếu header này thì endpoint (không AllowAnonymous) trả 401, mà request
      // là fire-and-forget lúc rời trang nên không ai thấy lỗi, số liệu chỉ lặng lẽ thiếu.
      // Đọc lại ở MỖI lần gửi để token luôn mới sau khi đăng nhập, giống apiClient.
      // CORS đã cho phép: CorsConfig.AllowedHeaders bên BE có 'hvs-token' và AllowCredentials.
      const token = Cookies.get(AUTHENTICATION_TOKEN);
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      // Khách vãng lai không có token và vẫn phải gửi được, nên chỉ thêm header khi thật sự có.
      if (token) headers['hvs-token'] = token;

      // Cần CẢ HAI lớp bắt lỗi, không phải thừa: try/catch bắt throw ĐỒNG BỘ (hiếm, vd fetch không
      // tồn tại hoặc CSP chặn ngay khi gọi), còn .catch() bắt promise bị reject (thường gặp hơn —
      // mất mạng, DNS lỗi, request bị huỷ giữa chừng). Thiếu lớp nào thì lỗi tương ứng lọt qua,
      // không rơi xuống được sendBeacon.
      try {
        fetch(url, {
          method: 'POST',
          keepalive: true,
          credentials: 'include',
          headers,
          body,
        }).catch(() => {
          // fetch keepalive thất bại (vd trình duyệt cũ) → đường lùi sendBeacon. Best-effort
          // thôi: sendBeacon không đặt được header nên KHÔNG có 'hvs-token', người đã đăng nhập
          // sẽ bị BE trả 401. Chỉ cứu được luồng khách vãng lai.
          navigator.sendBeacon?.(url, new Blob([body], { type: 'application/json' }));
        });
      } catch {
        // Cùng cảnh báo như trên: không có header, chỉ là cố thêm chứ không tương đương fetch.
        navigator.sendBeacon?.(url, new Blob([body], { type: 'application/json' }));
      }
    };

    const onVisibilityChange = () => {
      const now = Date.now();
      if (document.visibilityState === 'visible') counter.onVisible(now);
      else send(counter.onHidden(now));
    };

    const onPageHide = () => send(counter.takePending(Date.now()));

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);

    // Gỡ listener sạch sẽ: StrictMode chạy effect hai lần ở dev (mount/unmount/mount lại),
    // không gỡ sẽ gắn chồng listener và gửi trùng dữ liệu lên BE.
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, []);
};

export default useEngagementReporter;
